if (!customElements.get('product-carousel')) {
  class ProductCarousel extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('.slider__grid');
      this.slider = this.querySelector('.slider');

      // Async mode: the grid holds placeholders; fetch the real cards when the
      // carousel gets near the viewport, then shuffle and swap them in.
      if (this.dataset.asyncUrl && this.grid) {
        this.loadWhenNear();
        return;
      }

      // Shuffle products into a random order (reshuffled on every page load).
      if (this.dataset.shuffle === 'true' && this.grid) {
        this.shuffleItems();
      }

      this.fadeInImages();
      this.initAutoplay();
      this.initPeek();
    }

    /**
     * Fade card images in as they finish loading instead of letting them snap
     * in. The hiding CSS only applies under .pcar--fade-images, which is set
     * here, so without JS the images simply show. Images that are already
     * decoded (cached) are marked at once.
     */
    fadeInImages() {
      this.classList.add('pcar--fade-images');
      this.querySelectorAll('.card__main-image').forEach((img) => {
        const show = () => img.classList.add('is-loaded');
        if (img.complete && img.naturalWidth > 0) {
          show();
        } else {
          img.addEventListener('load', show, { once: true });
          img.addEventListener('error', show, { once: true });
        }
      });
    }

    initAutoplay() {
      this.bindArrows();
      // Auto-scroll.
      if (this.dataset.autoplay === 'true') {
        this.speed = parseInt(this.dataset.autoplaySpeed, 10) || 5000;
        this.nextBtn = this.querySelector('button[name="next"]');
        this.paused = false;
        this.bindPause();
        // Wait a tick so the carousel-slider has initialised its nav state.
        this.startTimer();
      }
    }

    loadWhenNear() {
      const load = () => this.loadCards();
      if (!('IntersectionObserver' in window)) {
        load();
        return;
      }
      const io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          io.disconnect();
          load();
        }
      }, { rootMargin: '250px 0px' });
      io.observe(this);
    }

    async loadCards() {
      if (this.loading) return;
      this.loading = true;
      let items = [];
      try {
        const res = await fetch(this.dataset.asyncUrl, { headers: { Accept: 'text/html' } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        items = Array.from(doc.querySelectorAll('li.pcar__item'));
      } catch (err) {
        console.warn('product-carousel: could not load products', err);
      }

      const count = parseInt(this.dataset.count, 10) || items.length;
      const visible = this.dataset.shuffle === 'true' ? this.shuffle(items).slice(0, count) : items.slice(0, count);

      if (visible.length === 0) {
        // Nothing to show: drop the placeholders and hide the whole section.
        this.grid.replaceChildren();
        const section = this.closest('.shopify-section');
        if (section) section.hidden = true;
        return;
      }

      this.settleReveal(visible);

      this.grid.replaceChildren(...visible.map((el) => document.adoptNode(el)));
      this.classList.add('pcar--loaded');
      this.fadeInImages();

      // The carousel-slider measures its slides on init; re-measure now that the
      // real cards are in place (if it hasn't initialised yet, it will do so
      // with the real cards).
      const slider = this.querySelector('carousel-slider');
      if (slider) {
        // It captured its slide list when constructed (on the placeholders).
        slider.slides = this.grid.children;
        if (slider.grid && typeof slider.initSlider === 'function') slider.initSlider();
      }

      this.initAutoplay();
      this.initPeek();
    }

    disconnectedCallback() {
      this.stopTimer();
    }

    shuffle(items) {
      // Fisher–Yates, shuffles the array in place.
      for (let i = items.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        const tmp = items[i];
        items[i] = items[j];
        items[j] = tmp;
      }
      return items;
    }

    shuffleItems() {
      // Prioritise in-stock products: shuffle the in-stock and out-of-stock
      // groups separately, show in-stock first, and only pad with out-of-stock
      // products to fill the carousel when there aren't enough in-stock ones.
      const all = Array.from(this.grid.children);
      const inStock = this.shuffle(all.filter((el) => el.dataset.available !== 'false'));
      const outOfStock = this.shuffle(all.filter((el) => el.dataset.available === 'false'));

      // Show `count` products, drawn at random from the in-stock group first
      // (it's already shuffled, so slicing gives a random pick of the whole
      // collection's in-stock products), then padded with out-of-stock fillers.
      const count = parseInt(this.dataset.count, 10) || all.length;
      const visible = inStock.slice(0, count);
      const fillers = Math.max(0, count - visible.length);
      visible.push(...outOfStock.slice(0, fillers));

      // Drop the products we won't display so they don't bloat the carousel.
      const keep = new Set(visible);
      all.forEach((el) => {
        if (!keep.has(el)) el.remove();
      });

      this.settleReveal(visible);

      const frag = document.createDocumentFragment();
      visible.forEach((el) => frag.appendChild(el));
      this.grid.appendChild(frag);
    }

    /**
     * Number of cards that fit in one page of the slider, measured from the
     * current children (placeholders or cards share the same width).
     */
    itemsPerPage() {
      const kids = this.grid.children;
      if (kids.length < 2) return kids.length || 4;
      const a = kids[0].getBoundingClientRect();
      const b = kids[1].getBoundingClientRect();
      const span = Math.abs(b.left - a.left) || a.width;
      if (!span) return 4;
      return Math.max(1, Math.round((this.grid.clientWidth + (span - a.width)) / span));
    }

    /**
     * The Liquid loop gives every card a fade-in reveal with a delay based on
     * its original index (up to ~3.5s for the 50th product). After shuffling,
     * keep the reveal only for the cards on the first page, re-staggered by
     * their new position, and show every other card outright so nothing fades
     * in later when the carousel scrolls or autoplays.
     */
    settleReveal(items) {
      const perPage = this.itemsPerPage();
      items.forEach((el, i) => {
        if (i < perPage) {
          if (el.hasAttribute('data-cc-animate')) {
            const delay = `${(i * 0.07).toFixed(2)}s`;
            el.dataset.ccAnimateDelay = delay;
            el.style.animationDelay = delay;
          }
        } else {
          el.removeAttribute('data-cc-animate');
          el.removeAttribute('data-cc-animate-delay');
          el.style.animationDelay = '';
          el.classList.remove('cc-animate-init', 'cc-animate-in');
        }
      });
    }

    /**
     * Briefly cross-fade one visible card at a time to its second image and
     * back, timed between autoplay steps so the section never moves two ways
     * at once. Touch screens never see the hover image otherwise; on desktop
     * it stops while the mouse is over the carousel so the hover takes over.
     */
    initPeek() {
      if (this.peekInit) return;
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduced || !this.grid) return;
      if (!this.grid.querySelector('.card__media.has-hover-image')) return;
      this.peekInit = true;
      this.classList.add('pcar--peek');
      this.peekFade = 600;
      this.peekHold = 2500;

      this.inView = false;
      if ('IntersectionObserver' in window) {
        new IntersectionObserver((entries) => {
          this.inView = entries.some((e) => e.isIntersecting);
        }, { threshold: 0.5 }).observe(this);
      } else {
        this.inView = true;
      }

      // A swipe, or the mouse arriving, cancels a running peek.
      if (this.slider) this.slider.addEventListener('scroll', () => this.endPeek(), { passive: true });
      this.addEventListener('pointerenter', () => this.endPeek());

      // After an arrow tap, reveal the card that came into view on that side.
      this.addEventListener('click', (evt) => {
        const btn = evt.target.closest('.slider-nav__btn');
        if (!btn || btn.disabled) return;
        const forward = btn.name === 'next';
        this.peekAfterScroll(forward ? 'revealed' : 'revealed-first');
      });

      // With autoplay, peeks follow each slide (see startTimer). Without it,
      // peek a random visible card on a slow rhythm instead.
      if (this.dataset.autoplay !== 'true') {
        this.peekTimer = setInterval(() => this.queuePeek(0), 5000);
        this.queuePeek(800);
      }
    }

    /**
     * Peek once the slider has stopped scrolling (an arrow step can take
     * longer than a fixed delay, and scrolling cancels a running peek).
     */
    peekAfterScroll(mode) {
      if (!this.peekInit || !this.slider) return;
      clearTimeout(this.peekQueued);
      clearTimeout(this.scrollIdle);
      if (this.onScrollIdle) this.slider.removeEventListener('scroll', this.onScrollIdle);
      const started = performance.now();
      const done = () => {
        this.slider.removeEventListener('scroll', this.onScrollIdle);
        this.onScrollIdle = null;
        this.queuePeek(150, mode, true);
      };
      this.onScrollIdle = () => {
        clearTimeout(this.scrollIdle);
        // Give up waiting after 2s (e.g. a continuous swipe).
        if (performance.now() - started > 2000) { done(); return; }
        this.scrollIdle = setTimeout(done, 150);
      };
      this.slider.addEventListener('scroll', this.onScrollIdle, { passive: true });
      // If nothing scrolls (already at the end), still settle.
      this.scrollIdle = setTimeout(done, 400);
    }

    queuePeek(delay, mode, fromUser) {
      if (!this.peekInit) return;
      clearTimeout(this.peekQueued);
      this.peekQueued = setTimeout(() => this.peek(mode, fromUser), delay);
    }

    visibleCards(withHoverImage = true) {
      if (!this.slider) return [];
      const box = this.slider.getBoundingClientRect();
      return Array.from(this.grid.children).filter((li) => {
        const r = li.getBoundingClientRect();
        const inside = r.left >= box.left - 2 && r.right <= box.right + 2;
        return inside && (!withHoverImage || li.querySelector('.card__media.has-hover-image'));
      });
    }

    peek(mode, fromUser) {
      // An arrow tap is explicit, so it peeks even while the pointer is over
      // the carousel (which pauses autoplay).
      if ((this.paused && !fromUser) || document.hidden || !this.inView || this.peeking) return;
      let li;
      if (mode === 'revealed' || mode === 'revealed-first') {
        // The card that just slid into view: the last fully visible one going
        // forward, the first going back. Skip if it has no second image.
        const cards = this.visibleCards(false);
        li = mode === 'revealed' ? cards.pop() : cards.shift();
        if (li && !li.querySelector('.card__media.has-hover-image')) li = null;
      } else {
        const candidates = this.visibleCards().filter((el) => el !== this.lastPeeked);
        li = candidates[Math.floor(Math.random() * candidates.length)];
      }
      if (!li) return;
      const media = li.querySelector('.card__media.has-hover-image');
      const img = media && media.querySelector('.card__hover-image');
      if (!img) return;

      const started = performance.now();
      this.peekPending = media;
      const show = () => {
        // Skip this round if the image took too long (a late peek would
        // overlap the next autoplay step) or it was cancelled meanwhile.
        if (this.peekPending !== media || performance.now() - started > 1200 || (this.paused && !fromUser) || !this.inView) {
          if (this.peekPending === media) this.peekPending = null;
          if (this.peeking !== media) media.classList.remove('is-peek-ready');
          return;
        }
        this.peekPending = null;
        this.peeking = media;
        this.lastPeeked = li;
        // Two frames so the transparent state is painted before fading in;
        // only if the peek wasn't cancelled in those frames.
        requestAnimationFrame(() => requestAnimationFrame(() => {
          if (this.peeking === media) media.classList.add('is-peeking');
        }));
        this.peekEnd = setTimeout(() => this.endPeek(), this.peekFade + this.peekHold);
      };
      // Only now is the second image displayed (still transparent), so only
      // the card actually peeked downloads it.
      media.classList.add('is-peek-ready');
      img.loading = 'eager';
      if (img.complete && img.naturalWidth > 0) show();
      else img.addEventListener('load', show, { once: true });
    }

    endPeek() {
      clearTimeout(this.peekEnd);
      // Cancel a peek whose image is still loading.
      if (this.peekPending) {
        const pending = this.peekPending;
        this.peekPending = null;
        if (this.peeking !== pending) pending.classList.remove('is-peek-ready');
      }
      if (this.peeking) {
        const media = this.peeking;
        media.classList.remove('is-peeking');
        this.peeking = null;
        // After the fade-out, hand the card back to the theme's own hover rules.
        setTimeout(() => {
          if (this.peeking !== media) media.classList.remove('is-peek-ready');
        }, this.peekFade + 50);
      }
    }

    bindArrows() {
      if (this.arrowsBound) return;
      this.arrowsBound = true;
      this.addEventListener('click', (evt) => {
        const btn = evt.target.closest('.slider-nav__btn');
        if (!btn) return;
        // Next automatic slide a full interval after the tap, so it never
        // cuts into the reveal.
        if (this.timer) this.startTimer();
        // On touch screens a tapped button keeps focus, which would pause
        // autoplay (focusin) until the visitor taps elsewhere.
        if (window.matchMedia('(hover: none), (pointer: coarse)').matches) {
          btn.blur();
          this.paused = false;
        }
      });
    }

    bindPause() {
      this.addEventListener('pointerenter', () => { this.paused = true; });
      this.addEventListener('pointerleave', () => { this.paused = false; });
      this.addEventListener('focusin', () => { this.paused = true; });
      this.addEventListener('focusout', () => { this.paused = false; });
    }

    startTimer() {
      this.stopTimer();
      this.timer = setInterval(() => {
        if (this.paused || document.hidden) return;
        // Let the slide settle, then reveal the second image of the card that
        // just came into view; the peek ends before the next slide. No peek
        // after looping back to the start.
        if (this.advance() === 'step') this.queuePeek(700, 'revealed');
      }, this.speed);
    }

    stopTimer() {
      if (this.timer) clearInterval(this.timer);
    }

    advance() {
      if (!this.slider || !this.grid) return;
      const items = this.grid.children;
      if (items.length < 2) return;

      // Width of one card + gap (so we always move exactly one product).
      const span = Math.round(
        items[1].getBoundingClientRect().left - items[0].getBoundingClientRect().left
      ) || Math.round(items[0].getBoundingClientRect().width);

      const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;

      if (this.slider.scrollLeft >= maxScroll - 4) {
        // Reached the end — loop back to the start.
        this.slider.scrollTo({ left: 0, behavior: 'smooth' });
        return 'loop';
      } else {
        // Step to the next card boundary; finish at the end when less than
        // half a card remains so the last card is shown in full.
        let target = (Math.round(this.slider.scrollLeft / span) + 1) * span;
        if (target >= maxScroll - span / 2) target = maxScroll;
        this.slider.scrollTo({ left: target, behavior: 'smooth' });
        return 'step';
      }
    }
  }

  customElements.define('product-carousel', ProductCarousel);
}
